import { useState, useEffect } from "react";
import { PortalChrome, HealthPill, StatusPill, MOCK, useRole } from "./_shared";
import { AlertTriangle, CheckCircle2, ChevronRight, Handshake, Package, Inbox, Activity, FileSignature } from "lucide-react";

// Option 3 — "Single-page flow" (Stripe Dashboard + Linear inspired)
// NO TABS. Every section is on one long scrollable page.
// Right-side sticky anchor nav jumps between sections.
// This is the "boring but professional" bet — used by Stripe, Linear, Vercel.

const SECTIONS = [
  { id: "overview",      label: "Overview",         icon: Handshake },
  { id: "relationships", label: "Relationships",    icon: Handshake },
  { id: "orders",        label: "Orders",           icon: Inbox },
  { id: "catalog",       label: "Catalog",          icon: Package },
  { id: "health",        label: "Health & claims",  icon: Activity },
  { id: "agreements",    label: "Agreements",       icon: FileSignature },
];

export default function Option3_SinglePage() {
  const [role, setRole] = useRole();
  const [active, setActive] = useState("overview");
  const m = MOCK[role];
  const isSeller = role === "seller";

  useEffect(() => {
    const onScroll = () => {
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top < 160 && rect.bottom > 200) { setActive(s.id); break; }
      }
    };
    const container = document.getElementById("opt3-scroll");
    container?.addEventListener("scroll", onScroll, { passive: true });
    return () => container?.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <PortalChrome role={role} setRole={setRole} optionLabel="Option 3 · Single-page flow (Stripe style)" optionNumber="3">
      <div className="border-b border-zinc-200 bg-white px-6 lg:px-8 py-5">
        <h1 className="text-2xl font-semibold tracking-tight">Dropship</h1>
        <p className="text-sm text-zinc-500 mt-0.5">
          {isSeller ? "Sell your catalog through buyer channels without holding their inventory." : "Add supplier products to your channels. Verqo keeps pricing, sync, invoices in one place."}
        </p>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Main scrollable content */}
        <main id="opt3-scroll" className="flex-1 overflow-y-auto bg-zinc-50">
          <div className="max-w-4xl mx-auto px-8 py-8 space-y-12">

            {/* OVERVIEW */}
            <section id="overview">
              <SectionHeader label="Overview" />
              <div className="grid grid-cols-4 gap-3">
                {m.kpis.map(k => (
                  <div key={k.label} className="rounded-2xl bg-white border border-zinc-200/80 p-4">
                    <div className="text-[11px] uppercase tracking-widest text-zinc-500">{k.label}</div>
                    <div className={`text-2xl font-semibold tracking-tight mt-1 ${k.accent === "warn" ? "text-amber-600" : "text-zinc-900"}`}>{k.value}</div>
                    {k.hint && <div className="text-xs text-zinc-500 mt-1">{k.hint}</div>}
                  </div>
                ))}
              </div>
            </section>

            {/* RELATIONSHIPS */}
            <section id="relationships">
              <SectionHeader label={m.partnersLabel} count={m.partners.length} />
              <div className="rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50/70 text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="text-left font-medium px-4 py-2.5">{m.partnerLabel}</th>
                      {isSeller && <th className="text-left font-medium px-4 py-2.5">Channel</th>}
                      <th className="text-right font-medium px-4 py-2.5">Orders 30d</th>
                      <th className="text-right font-medium px-4 py-2.5">GMV 30d</th>
                      <th className="text-left font-medium px-4 py-2.5">Sync</th>
                      <th className="text-left font-medium px-4 py-2.5">MAP</th>
                      <th className="text-left font-medium px-4 py-2.5">Credit used</th>
                      <th className="px-4 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.partners.map(p => (
                      <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50/60 cursor-pointer">
                        <td className="px-4 py-3 font-medium">{p.name}</td>
                        {isSeller && <td className="px-4 py-3 text-zinc-600 text-xs">{p.channel}</td>}
                        <td className="px-4 py-3 text-right tabular-nums">{p.orders30d}</td>
                        <td className="px-4 py-3 text-right tabular-nums">${p.gmv30d.toLocaleString()}</td>
                        <td className="px-4 py-3"><HealthPill kind={p.health}>{p.sync}</HealthPill></td>
                        <td className="px-4 py-3">{p.mapOk ? <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Clean</span> : <span className="text-[11px] text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Flag</span>}</td>
                        <td className="px-4 py-3 text-xs text-zinc-700 tabular-nums">{p.credit}</td>
                        <td className="px-4 py-3 text-right"><ChevronRight className="w-3.5 h-3.5 text-zinc-300 inline" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ORDERS */}
            <section id="orders">
              <SectionHeader label="Recent orders" count={m.orders.length} action="View all" />
              <div className="rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
                {m.orders.map((o, i) => (
                  <div key={o.id} className={`px-5 py-3.5 flex items-center gap-3 hover:bg-zinc-50/60 ${i > 0 ? "border-t border-zinc-100" : ""}`}>
                    <span className="font-mono text-[10px] text-zinc-500 w-20">{o.id}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{o.customer}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{o.partner} · {o.note}</div>
                    </div>
                    <span className="tabular-nums text-sm font-medium">${o.total.toFixed(2)}</span>
                    <StatusPill status={o.status} />
                    <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />
                  </div>
                ))}
              </div>
            </section>

            {/* CATALOG */}
            <section id="catalog">
              <SectionHeader label={isSeller ? "Catalog" : "Available from suppliers"} count={m.catalog.length} action={isSeller ? "Manage" : "Sync"} />
              <div className="rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50/70 text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="text-left font-medium px-4 py-2.5">SKU</th>
                      <th className="text-left font-medium px-4 py-2.5">Product</th>
                      {isSeller ? (
                        <>
                          <th className="text-right font-medium px-4 py-2.5">MSRP</th>
                          <th className="text-right font-medium px-4 py-2.5">MAP</th>
                          <th className="text-right font-medium px-4 py-2.5">Wholesale</th>
                          <th className="text-right font-medium px-4 py-2.5">Handling</th>
                          <th className="text-right font-medium px-4 py-2.5">Mirrored</th>
                        </>
                      ) : (
                        <>
                          <th className="text-left  font-medium px-4 py-2.5">Supplier</th>
                          <th className="text-right font-medium px-4 py-2.5">Wholesale</th>
                          <th className="text-right font-medium px-4 py-2.5">MSRP</th>
                          <th className="text-right font-medium px-4 py-2.5">Margin</th>
                          <th className="text-right font-medium px-4 py-2.5">Status</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {m.catalog.slice(0, 5).map(p => (
                      <tr key={p.sku} className="border-t border-zinc-100 hover:bg-zinc-50/60">
                        <td className="px-4 py-3 font-mono text-xs">{p.sku}</td>
                        <td className="px-4 py-3">{p.name}</td>
                        {isSeller ? (
                          <>
                            <td className="px-4 py-3 text-right tabular-nums">${p.msrp}</td>
                            <td className="px-4 py-3 text-right tabular-nums">${p.map}</td>
                            <td className="px-4 py-3 text-right tabular-nums">${p.wholesale}</td>
                            <td className="px-4 py-3 text-right tabular-nums">${p.handling}</td>
                            <td className="px-4 py-3 text-right"><span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100">{p.mirrored}</span></td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3 text-zinc-700">{p.supplier}</td>
                            <td className="px-4 py-3 text-right tabular-nums">${p.wholesale}</td>
                            <td className="px-4 py-3 text-right tabular-nums">${p.msrp}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">{Math.round(p.margin * 100)}%</td>
                            <td className="px-4 py-3 text-right"><span className={`text-[11px] px-2 py-0.5 rounded-full ${p.inStore ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100"}`}>{p.inStore ? "Live" : "Draft"}</span></td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* HEALTH */}
            <section id="health">
              <SectionHeader label="Health & claims" count={2} />
              <div className="space-y-2">
                <div className="rounded-2xl bg-white border border-zinc-200/80 p-4 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">CL-902 · Damaged on arrival</div>
                    <div className="text-xs text-zinc-500">Coastal Mercantile · 1d open</div>
                  </div>
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Refund pending</span>
                  <ChevronRight className="w-4 h-4 text-zinc-300" />
                </div>
                <div className="rounded-2xl bg-white border border-zinc-200/80 p-4 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">CL-901 · No carrier movement</div>
                    <div className="text-xs text-zinc-500">Urban Tots · 2d open</div>
                  </div>
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-zinc-100">Investigating</span>
                  <ChevronRight className="w-4 h-4 text-zinc-300" />
                </div>
              </div>
            </section>

            {/* AGREEMENTS */}
            <section id="agreements" className="pb-8">
              <SectionHeader label="Agreements" count={m.partners.length} />
              <div className="rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
                {m.partners.map((p, i) => (
                  <div key={p.id} className={`px-5 py-4 flex items-center gap-3 hover:bg-zinc-50/60 ${i > 0 ? "border-t border-zinc-100" : ""}`}>
                    <FileSignature className="w-4 h-4 text-zinc-400" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{p.name}</div>
                      <div className="text-[11px] text-zinc-500">Net 30 · $1.20 handling · MAP strict</div>
                    </div>
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${p.agreement === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                      {p.agreement}
                    </span>
                    <ChevronRight className="w-4 h-4 text-zinc-300" />
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>

        {/* Right anchor nav (sticky) */}
        <aside className="w-56 flex-shrink-0 border-l border-zinc-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-3">On this page</div>
          <div className="space-y-0.5 sticky top-4">
            {SECTIONS.map(s => {
              const Icon = s.icon;
              const isActive = active === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => jump(s.id)}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12.5px] transition ${
                    isActive ? "bg-zinc-900 text-white font-medium" : "text-zinc-600 hover:bg-zinc-50"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{s.label}</span>
                </button>
              );
            })}
          </div>
        </aside>
      </div>
    </PortalChrome>
  );
}

function SectionHeader({ label, count, action }) {
  return (
    <div className="flex items-end justify-between mb-3">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">
          {label}
          {count !== undefined && <span className="text-sm font-normal text-zinc-400 ml-2">{count}</span>}
        </h2>
      </div>
      {action && <button className="text-xs font-medium text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">{action}<ChevronRight className="w-3 h-3" /></button>}
    </div>
  );
}
